/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import ReactQuill, { Quill } from "react-quill";
import ReactDOM from "react-dom";
import { observer } from "mobx-react";
import "react-quill/dist/quill.snow.css";
import ImageResize from "quill-image-resize-module";
import ImageUploader from "quill-image-uploader";
import { ImageDrop } from "quill-image-drop-module";
import { makeApi, URLS } from "Utils/apiURL";
import { EditorUtils } from "Utils/EditorUtils";
import Utils from "Utils/utils";
import { fileAttachmentStore } from "Stores/FileAttachmentStore";
import { writingAssistantStoreData, defaultInputValue } from "Stores/WritingAssistantData";
import FileUpload from "Components/FileAttachment/FileUpload";
import FileAttachmentList from "Components/FileAttachment/FileAttachmentList";
import FileUploadingLoader from "Components/FileAttachment/FileUplaodingLoader";
import PerformTaskFileUploadingLoader from "Components/FileAttachment/PerformTaskFileUploadingLoader";
import TokenVariable from "Components/common/TokenVariable";
import { toasterState } from "Components/common/toaster";
import { MAX_IMAGE_SIZE_ALLOWED } from "Static/constant";
import { fileLimitExceedState } from "Components/FileAttachment/FileLimitExceedPopup";
import ToolbarTrackingIcon from "Components/common/ToolbarTrackingIcon";
import RewriteAi from "Components/WritingAssistant/ChatGptInEditor/RewriteAi";
import { ReactComponent as AiMagicWand } from "Assets/svg/WritingAssistant/AiMagicWand.svg";
import { ReactComponent as BulbPointerIcon } from "Assets/svg/bulbPointerIcon.svg";
import { ReactComponent as SpintaxIcon } from "Assets/svg/spintax/spintaxSvg.svg";
import { ReactComponent as WriteWithAiIcon } from "Assets/svg/spintax/writeWithAiSvg.svg";
import { ReactComponent as VariablesIcon } from "Assets/svg/spintax/variables.svg.svg";
import { ReactComponent as TokenVariableIcon } from "Assets/svg/tokenvariableIcon.svg";
import { ReactComponent as DownArrow } from "Assets/svg/donw-arrow.svg";
import { ReactComponent as DragHandleLinesIcon } from "Assets/svg/conditionBlock/dragHandleLines.svg";
import { ReactComponent as EditIcon } from "Assets/svg/conditionBlock/edit.svg";
import { ReactComponent as DeleteIcon } from "Assets/svg/conditionBlock/delete.svg";
import { ReactComponent as ChevronDownIcon } from "Assets/svg/spintax/chevronDown.svg";
import PerformTaskFileAttachment from "Components/FileAttachment/PerformTaskFileAttachment";
import { emailEditor } from "Stores/EmailEditor/EmailEditor";

var Block = Quill.import("blots/block");
let taskQuillRef = null;
let quillRef = null;

// Registering modules start
if (Block) {
  Block.tagName = "div";
  Quill.register(Block, true);
}

Quill.register("modules/imageResize", ImageResize, true);
Quill.register("modules/imageUploader", ImageUploader);
Quill.register("modules/imageDrop", ImageDrop);
let Link = Quill.import("formats/link");

const CustomLink = class extends Link {
  static create(value) {
    let node = super.create(value);
    node.setAttribute("href", value);
    return node;
  }

  static sanitize(url) {
    let decodedUrl = decodeURIComponent(url);
    let protocol = decodedUrl.slice(0, decodedUrl.indexOf(":"));
    if (this.PROTOCOL_WHITELIST.indexOf(protocol) === -1) {
      decodedUrl = "http://" + decodedUrl;
    }
    protocol = decodedUrl.slice(0, decodedUrl.indexOf(":"));
    return this.PROTOCOL_WHITELIST.indexOf(protocol) > -1
      ? decodedUrl
      : this.SANITIZED_URL;
  }
};

Quill.register(CustomLink, true);

var SizeStyle = Quill.import("attributors/style/size");
SizeStyle.whitelist = ["9.75px", "13px", "19.5px", "32.5px"];

let fonts = Quill.import("attributors/style/font");
fonts.whitelist = [
  "sans-serif",
  "serif",
  "Courier",
  "Georgia",
  "Gill Sans",
  "Garamond",
  "Helvetica",
  "monospace",
  "Palatino",
  "Tahoma",
  "Verdana"
];

let Inline = Quill.import("blots/inline");
const Variables = class extends Inline { };
Variables.blotName = "variables";
Variables.tagName = "span";
Quill.register(Variables, true);

Quill.register(SizeStyle, true);
Quill.register(fonts, true);
Quill.register(Link, true);
// Registering modules end

const Embed = Quill.import("blots/embed");

// Atomic inline chip blot — renders spintax as a clickable pill in the editor
class SpintaxChip extends Embed {
  static create(value) {
    const node = super.create();
    const options = Array.isArray(value?.options) ? value.options : [];
    node.setAttribute("data-options", JSON.stringify(options));
    node.setAttribute("contenteditable", "false");

    const text = document.createElement("span");
    text.className = "spintaxChipText";
    text.textContent = options[0] || "";

    const count = document.createElement("span");
    count.className = "spintaxChipCount";
    count.textContent = `· ${options.length}`;

    const arrow = document.createElement("span");
    arrow.className = "spintax-chip-arrow";
    ReactDOM.render(<ChevronDownIcon />, arrow);
    count.appendChild(arrow);

    node.appendChild(text);
    node.appendChild(count);
    return node;
  }

  static value(node) {
    try {
      return { options: JSON.parse(node.getAttribute("data-options") || "[]") };
    } catch {
      return { options: [] };
    }
  }
}
SpintaxChip.blotName = "spintaxChip";
SpintaxChip.tagName = "span";
SpintaxChip.className = "spintax-chip-blot";
Quill.register(SpintaxChip);

const Variations = class extends Inline { };
Variations.blotName = "variations";
Variations.tagName = "span";
Quill.register(Variations, true);

// Atomic inline chip blot for Dynamic Variables
class VariableChip extends Embed {
  static create(value) {
    const node = super.create();
    node.setAttribute("data-name", value);
    node.setAttribute("contenteditable", "false");
    node.textContent = value;
    return node;
  }

  static value(node) {
    return node.getAttribute("data-name");
  }
}
VariableChip.blotName = "variableChip";
VariableChip.tagName = "span";
VariableChip.className = "variable-chip-blot";
Quill.register(VariableChip);

class WorkingDaysChip extends Embed {
  static create(value) {
    const node = super.create();
    node.setAttribute("data-name", value);
    node.setAttribute("contenteditable", "false");
    node.textContent = value;
    return node;
  }

  static value(node) {
    return node.getAttribute("data-name");
  }
}
WorkingDaysChip.blotName = "workingDaysChip";
WorkingDaysChip.tagName = "span";
WorkingDaysChip.className = "working-days-chip-blot";
Quill.register(WorkingDaysChip);

let conditionInsertIndex = null;

class ConditionBlockBlot extends Embed {
  static create(data) {
    const node = super.create();
    node.setAttribute("data-branches", JSON.stringify(data.branches || []));
    node.setAttribute("data-fallback", data.fallback || "");
    node.setAttribute("contenteditable", "false");

    const branches = data.branches || [];

    const mainSection = document.createElement("span");
    mainSection.className = "cb-chip-main";

    const rhombusIcon = document.createElement("span");
    rhombusIcon.className = "cb-chip-rhombus";
    ReactDOM.render(<DragHandleLinesIcon />, rhombusIcon);

    const textSection = document.createElement("span");
    textSection.className = "cb-chip-text";
    textSection.textContent = "Condition";

    mainSection.appendChild(rhombusIcon);
    mainSection.appendChild(textSection);

    const sep1 = document.createElement("span");
    sep1.className = "cb-chip-sep";

    const editBtn = document.createElement("button");
    editBtn.className = "cb-chip-btn cb-chip-btn--edit";
    editBtn.setAttribute("data-action", "edit");
    editBtn.title = "Edit condition";
    ReactDOM.render(<EditIcon />, editBtn);

    const sep2 = document.createElement("span");
    sep2.className = "cb-chip-sep";

    const deleteBtn = document.createElement("button");
    deleteBtn.className = "cb-chip-btn cb-chip-btn--delete";
    deleteBtn.setAttribute("data-action", "delete");
    deleteBtn.title = "Remove";
    ReactDOM.render(<DeleteIcon />, deleteBtn);

    node.appendChild(mainSection);
    node.appendChild(sep1);
    node.appendChild(editBtn);
    node.appendChild(sep2);
    node.appendChild(deleteBtn);

    const openEditor = (e) => {
      e.stopPropagation();
      try {
        const branches = JSON.parse(node.getAttribute("data-branches") || "[]");
        const fallback = node.getAttribute("data-fallback") || "";
        window.dispatchEvent(new CustomEvent("openConditionBuilderForEdit", {
          detail: { branches, fallback, node }
        }));
      } catch (err) {
        console.error("Error reading condition blot data:", err);
      }
    };

    node.addEventListener("click", openEditor);

    deleteBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      const blot = Quill.find(node);
      if (blot) blot.remove();
    });

    editBtn.addEventListener("click", openEditor);

    return node;
  }
  static value(node) {
    try {
      return {
        branches: JSON.parse(node.getAttribute("data-branches") || "[]"),
        fallback: node.getAttribute("data-fallback") || "",
      };
    } catch {
      return { branches: [], fallback: "" };
    }
  }
}
ConditionBlockBlot.blotName = "conditionBlock";
ConditionBlockBlot.tagName = "span";
ConditionBlockBlot.className = "condition-block-blot";
Quill.register(ConditionBlockBlot, true);

const splitByPipeOutsideVars = (str) => {
  const parts = [];
  let current = "";
  let i = 0;
  while (i < str.length) {
    if (str[i] === "{" && str[i + 1] === "{") {
      const close = str.indexOf("}}", i + 2);
      if (close !== -1) {
        current += str.substring(i, close + 2);
        i = close + 2;
        continue;
      }
    } else if (str[i] === "{" && str[i + 1] !== "{") {
      let depth = 1;
      let j = i + 1;
      while (j < str.length && depth > 0) {
        if (str[j] === "{" && str[j + 1] === "{") {
          const close = str.indexOf("}}", j + 2);
          if (close !== -1) j = close + 1;
        } else if (str[j] === "{") depth++;
        else if (str[j] === "}") depth--;
        j++;
      }
      current += str.substring(i, j);
      i = j;
      continue;
    }

    if (str[i] === "|") {
      parts.push(current);
      current = "";
      i++;
      continue;
    }
    current += str[i];
    i++;
  }
  parts.push(current);
  return parts;
};

const convertSpintaxToChips = (quill) => {
  if (!quill) return false;
  const delta = quill.getContents();
  const conversions = [];

  let virtualText = "";
  const indexMap = [];
  let realIdx = 0;

  delta.ops.forEach(op => {
    if (typeof op.insert === "string") {
      for (let i = 0; i < op.insert.length; i++) {
        indexMap.push(realIdx + i);
        virtualText += op.insert[i];
      }
      realIdx += op.insert.length;
    } else if (op.insert && op.insert.spintaxChip) {
      const raw = `{${op.insert.spintaxChip.options.join("|")}}`;
      for (let i = 0; i < raw.length; i++) {
        indexMap.push(realIdx);
        virtualText += raw[i];
      }
      realIdx += 1;
    } else {
      indexMap.push(realIdx);
      virtualText += "\uFFFC";
      realIdx += 1;
    }
  });
  let i = 0;
  while (i < virtualText.length) {
    if (virtualText[i] === "{" && virtualText[i + 1] !== "{") {
      let depth = 1;
      let j = i + 1;
      let hasPipeAtLevel1 = false;
      while (j < virtualText.length && depth > 0) {
        if (virtualText[j] === "{" && virtualText[j + 1] === "{") {
          const close = virtualText.indexOf("}}", j + 2);
          if (close !== -1) j = close + 1;
        } else if (virtualText[j] === "{") depth++;
        else if (virtualText[j] === "}") depth--;
        else if (virtualText[j] === "|" && depth === 1) hasPipeAtLevel1 = true;
        j++;
      }
      if (depth === 0 && hasPipeAtLevel1) {
        const rawMatch = virtualText.substring(i, j);
        const content = rawMatch.substring(1, rawMatch.length - 1);
        const options = splitByPipeOutsideVars(content)
          .map(o => o.trim())
          .filter(Boolean);

        if (options.length >= 2) {
          const startIndex = indexMap[i];
          const endIndex = indexMap[j - 1];
          const length = (endIndex - startIndex) + 1;

          conversions.push({ index: startIndex, length, options });
        }
        i = j;
        continue;
      }
    }
    i++;
  }

  if (conversions.length === 0) return false;

  for (let k = conversions.length - 1; k >= 0; k--) {
    const { index, length, options } = conversions[k];
    try {
      quill.deleteText(index, length, "silent");
      quill.insertEmbed(index, "spintaxChip", { options }, "silent");
    } catch (err) {
      console.error("Spintax conversion error:", err);
    }
  }
  return true;
};

const convertVariablesToChips = (quill) => {
  if (!quill) return false;
  const delta = quill.getContents();
  const regex = /\{\{([^{}]+)\}\}/g;
  const conversions = [];
  let currentIndex = 0;

  delta.ops.forEach(op => {
    if (typeof op.insert === "string") {
      let match;
      while ((match = regex.exec(op.insert)) !== null) {
        conversions.push({
          index: currentIndex + match.index,
          length: match[0].length,
          name: match[1]
        });
      }
      currentIndex += op.insert.length;
    } else {
      currentIndex += 1;
    }
  });

  if (conversions.length === 0) return false;

  const workingDaysRegex = /^(\d+|x) (working days|days) from today$/i;
  for (let k = conversions.length - 1; k >= 0; k--) {
    const { index, length, name } = conversions[k];
    try {
      quill.deleteText(index, length, "silent");
      if (workingDaysRegex.test(name)) {
        quill.insertEmbed(index, "workingDaysChip", name, "silent");
      } else {
        quill.insertEmbed(index, "variableChip", name, "silent");
      }
    } catch (err) {
      console.error("Variable conversion error:", err);
    }
  }
  return true;
};

const getDeltaSliceAsHtml = (quill, index, length) => {
  if (length <= 0) return "";
  const delta = quill.getContents(index, length);
  const tempEl = document.createElement("div");
  new Quill(tempEl).setContents(delta);
  let html = tempEl.querySelector(".ql-editor")?.innerHTML || "";

  if (html.startsWith("]")) {
    html = html.substring(1);
  } else if (html.startsWith("<div>]")) {
    html = html.replace("<div>]", "<div>");
  }

  return html;
};

const convertConditionMarkersToBlots = (quill) => {
  if (!quill) return;
  const children = Array.from(quill.root.querySelectorAll("p, div"))
    .filter(n => n !== quill.root && !n.closest(".condition-block-blot"));
  const ifNodeIdx = children.findIndex(n =>
    /\[\[IF:/.test(n.textContent || "")
  );
  if (ifNodeIdx === -1) return;
  const blockNodes = [];
  for (let i = ifNodeIdx; i < children.length; i++) {
    blockNodes.push(children[i]);
    if ((children[i].textContent || "").includes("[[ENDIF]]")) break;
  }

  if (!(blockNodes[blockNodes.length - 1].textContent || "").includes("[[ENDIF]]")) return;

  const branches = [];
  let fallback = "";
  let currentBranch = null;
  let currentNodes = [];
  let inFallback = false;

  const flushContent = () => {
    while (currentNodes.length && !(currentNodes[0].textContent || "").trim()) {
      currentNodes.shift();
    }
    return currentNodes.map(n => n.outerHTML || "").join("");
  };

  for (const n of blockNodes) {
    const rawText = (n.textContent || "").trim();
    const markerMatch = rawText.match(/\[\[(?:IF|ELSEIF|ELSE|ENDIF)[^\]]*\]\]/);
    const t = markerMatch ? markerMatch[0] : rawText;

    if (t === "[[ENDIF]]") {
      if (inFallback) fallback = flushContent();
      else if (currentBranch) currentBranch.content = flushContent();
      break;
    }
    if (t === "[[ELSE]]") {
      if (currentBranch) currentBranch.content = flushContent();
      inFallback = true;
      currentNodes = [];
      continue;
    }
    if (t.startsWith("[[IF:") || t.startsWith("[[ELSEIF:")) {
      if (currentBranch) currentBranch.content = flushContent();
      const match = t.match(/^\[\[(?:IF|ELSEIF):([^:]+):([^:]+)(?::(.*))?]]$/);
      currentBranch = {
        field: match?.[1] || "",
        operator: match?.[2] || "",
        value: match?.[3] || "",
        content: "",
      };
      branches.push(currentBranch);
      currentNodes = [];
      continue;
    }

    currentNodes.push(n);
  }

  if (!branches.length) return;

  const startBlot = Quill.find(blockNodes[0]);
  const endBlot = Quill.find(blockNodes[blockNodes.length - 1]);

  if (!startBlot || !endBlot) return;

  const startIndex = quill.getIndex(startBlot);
  const deleteLength = quill.getIndex(endBlot) - startIndex + endBlot.length();

  quill.deleteText(startIndex, deleteLength, "silent");
  quill.insertEmbed(startIndex, "conditionBlock", { branches, fallback }, "silent");
  quill.insertText(startIndex + 1, "\n", "silent");
  requestAnimationFrame(() => convertConditionMarkersToBlots(quill));
};


const serializeEditorContent = (quill) => {
  if (!quill) return "";
  const quillDelta = quill.getContents();
  const test = JSON.parse(JSON.stringify(quillDelta));
  const tempCont = document.createElement("div");
  new Quill(tempCont).setContents(test);
  const chips = tempCont.querySelectorAll(".spintax-chip-blot");
  chips.forEach(chip => {
    try {
      const options = JSON.parse(chip.getAttribute("data-options") || "[]");
      chip.parentNode.replaceChild(
        document.createTextNode(`{${options.join("|")}}`),
        chip
      );
    } catch { }
  });
  const varChips = tempCont.querySelectorAll(".variable-chip-blot");
  varChips.forEach(chip => {
    const name = chip.getAttribute("data-name");
    chip.parentNode.replaceChild(
      document.createTextNode(`{{${name}}}`),
      chip
    );
  });
  const workingDaysChips = tempCont.querySelectorAll(".working-days-chip-blot");
  workingDaysChips.forEach(chip => {
    const name = chip.getAttribute("data-name");
    chip.parentNode.replaceChild(
      document.createTextNode(`{{${name}}}`),
      chip
    );
  });

  const condBlots = Array.from(tempCont.querySelectorAll(".condition-block-blot"));
  condBlots.forEach((blot) => {
    try {
      const branches = JSON.parse(blot.getAttribute("data-branches") || "[]");
      const fallback = blot.getAttribute("data-fallback") || "";

      const markerNodes = [];
      branches.forEach((branch, i) => {
        const tag = i === 0 ? "IF" : "ELSEIF";
        const markerDiv = document.createElement("div");
        markerDiv.textContent = `[[${tag}:${branch.field}:${branch.operator}:${branch.value || ""}]]`;
        markerNodes.push(markerDiv);
        const contentTmp = document.createElement("div");
        contentTmp.innerHTML = branch.content || "";
        Array.from(contentTmp.childNodes).forEach(n => markerNodes.push(n));
      });
      if (fallback !== "") {
        const elseDiv = document.createElement("div");
        elseDiv.textContent = "[[ELSE]]";
        markerNodes.push(elseDiv);
        const fallbackTmp = document.createElement("div");
        fallbackTmp.innerHTML = fallback;
        Array.from(fallbackTmp.childNodes).forEach(n => markerNodes.push(n));
      }
      const endifDiv = document.createElement("div");
      endifDiv.textContent = "[[ENDIF]]";
      markerNodes.push(endifDiv);

      const parent = blot.parentNode;
      const grandParent = parent?.parentNode;

      if (grandParent && parent !== tempCont) {
        // The condition is an inline embed that can sit mid-paragraph
        // (<p>lead [cond] trail</p>). Split the parent at the blot so the
        // block-level markers land exactly where the condition was: leading
        // text stays in `parent`, markers go right after it, and any trailing
        // siblings move into a cloned tail node after the markers. Inserting
        // the markers before the whole parent (the old behaviour) reordered
        // the condition ahead of its leading text, shifting it on reopen.
        const refNode = parent.nextSibling;
        const trailingNodes = [];
        let sib = blot.nextSibling;
        while (sib) {
          const next = sib.nextSibling;
          trailingNodes.push(sib);
          sib = next;
        }
        markerNodes.forEach(node => grandParent.insertBefore(node, refNode));
        if (trailingNodes.length) {
          const tail = parent.cloneNode(false);
          trailingNodes.forEach(n => tail.appendChild(n));
          grandParent.insertBefore(tail, refNode);
        }
        parent.removeChild(blot);
        if (!parent.textContent.trim() && parent.children.length === 0) {
          grandParent.removeChild(parent);
        }
      } else {
        markerNodes.forEach(node => parent.insertBefore(node, blot));
        parent.removeChild(blot);
      }
    } catch (err) {
      console.error("Error serializing condition block:", err);
    }
  });
  return tempCont.querySelector(".ql-editor")?.innerHTML || tempCont?.innerHTML || "";
};

const modules = {
  toolbar: [
    ["bold", "italic", "image"],
    ["link"],
    [
      {
        color: [
          "#000000",
          "#e60000",
          "#ff9900",
          "#ffff00",
          "#008a00",
          "#0066cc",
          "#9933ff",
          "#ffffff",
          "#facccc",
          "#ffebcc",
          "#ffffcc",
          "#cce8cc",
          "#cce0f5",
          "#ebd6ff",
          "#bbbbbb",
          "#f06666",
          "#ffc266",
          "#ffff66",
          "#66b966",
          "#66a3e0",
          "#c285ff",
          "#888888",
          "#a10000",
          "#b26b00",
          "#b2b200",
          "#006100",
          "#0047b2",
          "#6b24b2",
          "#444444",
          "#5c0000",
          "#663d00",
          "#666600",
          "#003700",
          "#002966",
          "#3d1466"
        ]
      }
    ],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ font: fonts.whitelist }],
    [{ align: [] }],
    [{ size: ["9.75px", "13px", "19.5px", "32.5px"] }],
    ["variations"]
  ],
  clipboard: {
    matchVisual: false
  },
  imageUploader: {
    upload: file => {
      return new Promise((resolve, reject) => {
        if (emailEditor?.dropFile) {
          reject();
          if (emailEditor?.imgCount >= 1) {
            setTimeout(() => {
              emailEditor.setDropFile(false);
              emailEditor.setImgCount(0);
            }, 50);
          } else {
            emailEditor.setDropFile(false);
          }
          toasterState.setToastMsg(
            "Please use the image uploader button to add images to the email.",
            "failure"
          );
          return;
        }
        if (validateImage(file)) {
          const formData = new FormData();
          formData.append("file", file);
          const config = {
            url: URLS.uploadImage ? URLS.uploadImage : "",
            method: "POST",
            data: formData
          };
          makeApi(config)
            .then(result => {
              if (+result?.request?.status === 417) {
                resolve("");
                toasterState.setToastMsg(
                  `Invalid image format. Please try again with a supported formats (.png, .jpg, .jpeg, .gif).`,
                  "fail"
                );
              } else {
                if (result?.data?.url) {
                  resolve(result.data.url);
                } else {
                  resolve("");
                  toasterState.setToastMsg(
                    `Image upload failed. Please try again.`,
                    "fail"
                  );
                }
              }
            })
            .catch(error => {
              reject("");
              toasterState.setToastMsg(
                `Image upload failed. Please try again.`,
                "fail"
              );
              return "";
            });
        } else {
          reject();
        }
      });
    }
  },
  imageResize: {
    parchment: Quill.import("parchment"),
    modules: ["Resize", "DisplaySize"]
  }
};

const validateImage = file => {
  if (file) {
    const { size } = file;
    const sizeInMb = size / 1024 / 1024;
    if (sizeInMb < MAX_IMAGE_SIZE_ALLOWED) {
      return true;
    } else {
      showImageUploadError();
    }
  }
  return false;
};

const showImageUploadError = () => {
  fileLimitExceedState.setMessage(
    `Image size should not be more than 3MB. Please reduce the file size and try again.`
  );
  fileLimitExceedState.setType("image");
  fileLimitExceedState.setShowPopup(true);
};

// Collapsible component for previous content (like Gmail)
const CollapsiblePrevContent = ({
  content = "",
  onExpandChange,
  initialExpanded = false,
  lastUpdatedTs = "",
  repliedFromAddress = ""
}) => {
  const [isExpanded, setIsExpanded] = useState(initialExpanded);

  useEffect(() => {
    if (initialExpanded && !isExpanded) {
      setIsExpanded(true);
      if (onExpandChange) {
        onExpandChange(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialExpanded]);

  const handleToggle = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    if (onExpandChange) {
      onExpandChange(newExpandedState);
    }
  };

  return (
    <div className="previousReplyContentWrapper">
      {!isExpanded ? (
        <div className="previousReplyContentCollapsed">
          <div className="threeDots" onClick={handleToggle}>
            <span className="dot"></span>
            <span className="dot"></span>
            <span className="dot"></span>
          </div>
        </div>
      ) : (
        <div className="previousReplyContent">
          <div className="previousReplyContentHeader">
            <div className="threeDots" onClick={handleToggle}>
              <span className="dot"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </div>
          </div>
          <div className="previousReplyContentBody">
            {lastUpdatedTs && (
              <div className="previousReplyContentTimestamp">
                {lastUpdatedTs}
                {repliedFromAddress && (
                  <>
                    {" <"}
                    <a
                      href={`mailto:${repliedFromAddress}`}
                      target="_blank"
                      rel="noreferrer noopener nofollow"
                    >
                      {repliedFromAddress}
                    </a>
                    {"> "}
                    wrote:
                  </>
                )}
              </div>
            )}
            <div
              dangerouslySetInnerHTML={{
                __html: Utils.contentReadyOnly(content)
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const QuilEditor = (props = {}) => {
  const {
    attachments = [],
    entityObj = {},
    pageType = "",
    stageType = "",
    isSubjectFocused = false,
    prefilledValue = "",
    type = "",
    fromTaskPage = false,
    isOutboxPage = false,
    showRewriteAI = false,
    bodyOnChange = () => { },
    setsubjectFocus = () => { },
    setSubject = () => { },
    updateContentCbk = false,
    placeholder = "",
    replyContent = "",
    emailType = "",
    emailSubject = "",
    replyAsThread = false,
    lastUpdatedTs = "",
    repliedFromAddress = "",
    showInboxTokens = true,
    showDateTokens = true,
    inboxIds = [],
    showVariations = false,
    showAddCondition = false,
    disableSpintaxAndCondition = false
  } = props || {};

  const editorModules = React.useMemo(() => {
    if (showVariations) return modules;
    const newToolbar = modules.toolbar.map(row =>
      row.filter(item => item !== "variations")
    ).filter(row => row.length > 0);
    return { ...modules, toolbar: newToolbar };
  }, [showVariations]);

  let reactQuillRef = (fromTaskPage ? taskQuillRef : quillRef) || null;
  const [isLimitReached, setIsLimitReached] = useState(false);

  const checkConditionLimit = () => {
    const _cq = fromTaskPage ? taskQuillRef : quillRef;
    if (_cq) {
      const count = _cq.root.querySelectorAll(".condition-block-blot").length;
      setIsLimitReached(count >= 3);
    }
  };

  const [showVariableCont, setshowVariableCont] = useState(false);
  const [showVariationsDropdown, setShowVariationsDropdown] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [tokenPopupPosition, setTokenPopupPosition] = useState({ top: 0, left: 0 });
  const [isFlipped, setIsFlipped] = useState(false);
  const [triggerType, setTriggerType] = useState(null); // 'keyboard' | 'toolbar'

  // Chip popover state
  const [showChipPopover, setShowChipPopover] = useState(false);
  const [chipPopoverData, setChipPopoverData] = useState(null); // { options: string[] }
  const [chipPopoverPosition, setChipPopoverPosition] = useState({ top: 0, left: 0 });
  const chipElementRef = useRef(null);

  // Working days chip popover state
  const [showWorkingDaysPopover, setShowWorkingDaysPopover] = useState(false);
  const [workingDaysCount, setWorkingDaysCount] = useState(1);
  const [workingDaysType, setWorkingDaysType] = useState("working"); // "working" | "calendar"
  const [workingDaysPopoverPosition, setWorkingDaysPopoverPosition] = useState({ top: 0, left: 0 });
  const workingDaysChipRef = useRef(null);

  const triggerIndexRef = useRef(0);
  const [fileUploading, setFileUploading] = useState(false);
  const previousReplyContentExpandedRef = useRef(false);
  const previousReplyContentRef = useRef(null);
  const hoverTimeout = useRef(null);

  const clearHoverTimeout = () => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
  };

  const startHoverTimeout = () => {
    clearHoverTimeout();
    hoverTimeout.current = setTimeout(() => {
      setshowVariableCont(false);
    }, 100);
  };

  useEffect(() => {
    if (showVariationsDropdown) {
      checkConditionLimit();
    }
  }, [showVariationsDropdown]);

  const bindCustomEvent = () => {
    var variationsButton = document.querySelector(".ql-variations");
    if (variationsButton) {
      const handleAction = () => {
        const rect = variationsButton.getBoundingClientRect();
        const quillContainer = document.querySelector(".quillEditorContainer");
        const containerRect = quillContainer
          ? quillContainer.getBoundingClientRect()
          : { top: 0, left: 0, height: 0 };
        const containerHeight = containerRect.height || 0;
        const DROPDOWN_HEIGHT = 230;

        let top = rect.top - containerRect.top - DROPDOWN_HEIGHT - 5;
        let left = rect.left - containerRect.left;
        let flipped = false;

        if (top < 0) {
          const belowTop = (rect.bottom - containerRect.top) + 10;
          if (belowTop + DROPDOWN_HEIGHT <= containerHeight) {
            top = belowTop;
            flipped = true;
          } else {
            top = Math.max(0, rect.top - containerRect.top - DROPDOWN_HEIGHT - 5);
          }
        }

        const editorWidth = containerRect.width || 0;
        if (left + 260 > editorWidth) left = editorWidth - 270;
        if (left < 0) left = 10;

        setIsFlipped(flipped);
        setPopupPosition({ top, left });
        setTriggerType("toolbar");

        const range = reactQuillRef?.getSelection();
        if (range) triggerIndexRef.current = range.index;
      };

      variationsButton.addEventListener("click", function (event) {
        event.preventDefault();
        handleAction();
        setShowVariationsDropdown(prev => !prev);
      });
      ReactDOM.render(
        <>
          <TokenVariableIcon />
          <span className="variationsText">Variations</span>
          <span className="variationsArrow">▾</span>
        </>,
        variationsButton
      );
    }
  };

  const optionAction = (value = "") => {
    if (window && window.getSelection()) {
      window.getSelection().deleteFromDocument();
    }

    const currentRange = reactQuillRef.getSelection();
    let effectiveIndex = triggerIndexRef.current;
    if (triggerType === "toolbar" && currentRange) {
      effectiveIndex = currentRange.index;
    }

    if (isSubjectFocused) {
      setSubject(value);
    } else if (value?.length) {
      let insertIndex = effectiveIndex;
      if (triggerType === "keyboard") {
        const textBefore = reactQuillRef.getText(effectiveIndex - 2, 2);
        if (textBefore === "{{") {
          reactQuillRef.deleteText(effectiveIndex - 2, 2, "user");
          insertIndex = effectiveIndex - 2;
          reactQuillRef.insertText(insertIndex, value, "user");
        } else {
          reactQuillRef.insertText(insertIndex, value, "user");
        }
      } else if (triggerType === "chip") {
        reactQuillRef.deleteText(effectiveIndex, 1, "user");
        reactQuillRef.insertText(insertIndex, value, "user");
      } else {
        reactQuillRef.insertText(insertIndex, value, "user");
      }
      convertVariablesToChips(reactQuillRef);
      reactQuillRef.setSelection(insertIndex + 1, 0, "user");
    }
    setTriggerType(null);
    setshowVariableCont(false);
    setShowVariationsDropdown(false);
  };

  const handleCloseVariations = (e) => {
    e && e.stopPropagation();
    if (triggerType === "keyboard") {
      const textBefore = reactQuillRef.getText(triggerIndexRef.current - 2, 2);
      if (textBefore === "{{") {
        reactQuillRef.deleteText(triggerIndexRef.current - 2, 2);
      }
    }
    setTriggerType(null);
    setShowVariationsDropdown(false);
    setshowVariableCont(false);
  };

  const handleCloseVariables = () => {
    if (triggerType === "keyboard") {
      const textBefore = reactQuillRef.getText(triggerIndexRef.current - 2, 2);
      if (textBefore === "{{") {
        reactQuillRef.deleteText(triggerIndexRef.current - 2, 2);
      }
    }
    setTriggerType(null);
    setshowVariableCont(false);
    setShowVariationsDropdown(false);
  };

  const addLinkTitleTooltip = () => {
    let toolbarElement = document.querySelector(".ql-toolbar .ql-link");
    toolbarElement.setAttribute("title", "Insert a link");
  };

  const attachQuillRefs = (el = "") => {
    if (
      ((fromTaskPage && taskQuillRef === null) ||
        (!fromTaskPage && quillRef === null)) &&
      el &&
      typeof el?.getEditor === "function"
    ) {
      addLinkTitleTooltip();
      if (fromTaskPage) {
        taskQuillRef = el.getEditor();
      } else {
        quillRef = el.getEditor();
      }
      reactQuillRef = fromTaskPage ? taskQuillRef : quillRef;
      reactQuillRef.focus();
    }
  };

  const handleChange = (editorValue, delta, source) => {
    if (reactQuillRef) {
      if (source === "user") {
        const isSpintaxManualTrigger = delta.ops && delta.ops.some(op =>
          typeof op.insert === "string" && /[{|}]/.test(op.insert)
        );
        if (isSpintaxManualTrigger) {
          writingAssistantStoreData.setSpintaxSeed(Date.now());
          writingAssistantStoreData.clearSamples();
          convertSpintaxToChips(reactQuillRef);
        } else {
          writingAssistantStoreData.setSampleParsedSpintax("");
          writingAssistantStoreData.setSampleParsedSubject("");
        }
      }
      if (editorValue && editorValue.indexOf("image-uploading") === -1) {
        bodyOnChange(serializeEditorContent(reactQuillRef), source);
      }
      if (source === "user") {
        convertVariablesToChips(reactQuillRef);
      }
      checkConditionLimit();
      if (source === "user") {
        const selection = reactQuillRef.getSelection();
        if (selection) {
          const textBeforeTwo = reactQuillRef.getText(selection.index - 2, 2);
          const textBeforeOne = reactQuillRef.getText(selection.index - 1, 1);

          if (textBeforeTwo === "{{") {
            setShowChipPopover(false);

            const len = 2;
            reactQuillRef.formatText(selection.index - len, len, { color: "#3953fb" });
            reactQuillRef.format("color", false);

            if (!fromTaskPage) {
              const range = reactQuillRef.getSelection();
              const bounds = reactQuillRef.getBounds(range.index);
              const quillContainer = document.querySelector(".quillEditorContainer");
              const editorWidth = quillContainer ? quillContainer.offsetWidth : 0;
              const containerHeight = quillContainer ? quillContainer.offsetHeight : 0;
              const scrollAmount = reactQuillRef.scrollingContainer.scrollTop;
              const DROPDOWN_HEIGHT = 230;

              let viewportTop = bounds.top - scrollAmount;
              let top = viewportTop - DROPDOWN_HEIGHT - 5;
              let left = bounds.left;
              let flipped = false;

              if (top < 0) {
                const belowTop = (bounds.bottom - scrollAmount) + 10;
                if (belowTop + DROPDOWN_HEIGHT <= containerHeight) {
                  top = belowTop;
                  flipped = true;
                } else {
                  top = Math.max(0, viewportTop - DROPDOWN_HEIGHT - 5);
                }
              }
              if (left + 260 > editorWidth) left = editorWidth - 270;
              if (left < 0) left = 10;

              setIsFlipped(flipped);
              setTriggerType("keyboard");
              triggerIndexRef.current = range.index;
              setPopupPosition({ top, left });
              setShowVariationsDropdown(true);
            }
          } else if (showVariationsDropdown && triggerType === "keyboard") {
            const triggerTextOne = reactQuillRef.getText(triggerIndexRef.current - 1, 1);
            const triggerTextTwo = reactQuillRef.getText(triggerIndexRef.current - 2, 2);
            if ((triggerTextOne !== "{" && triggerTextTwo !== "{{") || selection.index < triggerIndexRef.current - 1) {
              setShowVariationsDropdown(false);
              setTriggerType(null);
            }
          }
        }
      }
    }
  };

  const scrollIntoContentView = () => {
    if (reactQuillRef) {
      let scrollingContainer = reactQuillRef?.scrollingContainer;
      scrollingContainer.scrollTop = scrollingContainer.scrollHeight;
    }
  };

  const applyTypewriterEffect = async (objList = {}) => {
    let tempObj = { ops: [] };

    const delayedDirectInsertion = () => {
      return new Promise(resolve => {
        setTimeout(() => {
          reactQuillRef.setContents(tempObj);
          scrollIntoContentView();
          resolve();
        }, 10);
      });
    };

    const delayedInsertion = (obj = {}, insert = "", index = "") => {
      return new Promise(resolve => {
        setTimeout(() => {
          tempObj.ops[index] = { ...obj, insert };
          reactQuillRef.setContents(tempObj);
          scrollIntoContentView();
          resolve();
        }, 10);
      });
    };

    const performInsertion = async (obj = {}, insert = "", index = "") => {
      let text = "";
      for (let letter of insert) {
        text += letter;
        await delayedInsertion(obj, text, index);
      }
    };

    if (
      objList &&
      Object.keys(objList)?.length > 0 &&
      objList?.ops?.length > 0
    ) {
      let tempList = objList?.ops || [];
      if (tempList?.length > 0) {
        for (let index = 0; index < tempList?.length; index++) {
          const ele = tempList?.[index] || {};
          const { insert = "" } = ele || {};
          if (typeof insert === "string" && insert) {
            await performInsertion(ele, insert, index);
          } else {
            tempObj.ops[index] = ele;
            await delayedDirectInsertion(tempObj);
          }
        }
      }
    }
  };

  const applyTypewriteEffectForChatGptContent = async (data = {}) => {
    writingAssistantStoreData.setHideChatGpt(true);
    writingAssistantStoreData.setTypewriteAnimation(false);
    await applyTypewriterEffect(data);
    setTimeout(() => {
      if (reactQuillRef) {
        convertConditionMarkersToBlots(reactQuillRef);
        convertSpintaxToChips(reactQuillRef);
        convertVariablesToChips(reactQuillRef);
      }
    }, 0);
    writingAssistantStoreData.setHideChatGpt(false);
  };

  const prefillData = () => {
    if (reactQuillRef) {
      // Normalize truly-empty <div></div> to <div><br></div> so Quill's clipboard
      // converter always produces a consistent \n paragraph for them. Without this,
      // Quill silently drops empty divs causing index mismatches in condition block parsing.
      const normalizedValue = (prefilledValue || "").replace(/<div><\/div>/g, "<div><br></div>");
      const dom = document.createElement("div");
      dom.innerHTML =
        normalizedValue?.replaceAll(/\s*<div>\s*\t*<\/div>/g, "") || "";
      dom.innerHTML =
        EditorUtils.replaceEmptyContentHtmlWithBreakTag(normalizedValue);
      if (
        dom?.firstChild &&
        prefilledValue &&
        dom?.firstChild?.nodeName !== "#text"
      ) {
        const insertTarget = dom.firstChild?.classList?.contains("ql-editor")
          ? dom.firstChild
          : dom;
        insertTarget.insertAdjacentHTML("beforeend", "<div><br></div>");
      }
      if (showRewriteAI) {
        if (fromTaskPage) {
          writingAssistantStoreData.setTaskBodyContent(prefilledValue || "");
        } else {
          writingAssistantStoreData.setBodyContent(prefilledValue || "");
        }
      }
      const constructedDelta =
        reactQuillRef.clipboard.convert(
          fromTaskPage || isOutboxPage
            ? normalizedValue
            : dom?.innerHTML || normalizedValue
        ) || [];

      if (writingAssistantStoreData.typewriteAnimation) {
        applyTypewriteEffectForChatGptContent(constructedDelta);
      } else {
        reactQuillRef.setContents(constructedDelta);
        convertConditionMarkersToBlots(reactQuillRef);
        convertSpintaxToChips(reactQuillRef);
        convertVariablesToChips(reactQuillRef);
      }
      checkConditionLimit();
    }
  };

  const handleFocus = () => {
    isSubjectFocused && setsubjectFocus(false);
  };

  const handleSpintaxClick = async (passedIndex = null) => {
    const currentTriggerType = triggerType;
    setTriggerType(null);
    if (reactQuillRef) {
      const currentRange = reactQuillRef.getSelection();
      let insertIndex = passedIndex !== null ? passedIndex : triggerIndexRef.current;

      if (currentTriggerType === "toolbar" && currentRange && passedIndex === null) {
        insertIndex = currentRange.index;
      }

      // Calculate position for popover relative to insertion point
      const bounds = reactQuillRef.getBounds(insertIndex);
      const container = document.querySelector(".quillEditorContainer");
      const containerRect = container ? container.getBoundingClientRect() : { top: 0, left: 0 };
      const scrollAmount = reactQuillRef.scrollingContainer.scrollTop;

      setChipPopoverPosition({
        top: bounds.bottom - scrollAmount + 6,
        left: Math.max(10, Math.min(bounds.left, (container?.offsetWidth || 800) - 320))
      });

      triggerIndexRef.current = insertIndex; // Store index for application
      setChipPopoverData({ options: ["Option 1", "Option 2", "Option 3"] });
      setShowChipPopover(true);
      chipElementRef.current = null; // Indicates creating NEW spintax
    }
  };

  // Apply updated options back to the chip in the editor
  const handleChipPopoverApply = () => {
    if (!reactQuillRef) return;
    const validOptions = (chipPopoverData?.options || []).filter(o => o.trim());
    if (validOptions.length < 2) {
      setShowChipPopover(false);
      return;
    }
    try {
      if (chipElementRef.current) {
        const blot = Quill.find(chipElementRef.current);
        if (blot) {
          const index = reactQuillRef.getIndex(blot);
          reactQuillRef.deleteText(index, 1, "silent");
          reactQuillRef.insertEmbed(index, "spintaxChip", { options: validOptions }, "silent");
          reactQuillRef.setSelection(index + 1, "silent");
        }
      } else {
        // Handle new spintax creation
        let insertIndex = triggerIndexRef.current;

        // Check if we need to remove trigger characters ({ or {{)
        const textBeforeTwo = reactQuillRef.getText(insertIndex - 2, 2);
        const textBeforeOne = reactQuillRef.getText(insertIndex - 1, 1);

        if (textBeforeTwo === "{{") {
          reactQuillRef.deleteText(insertIndex - 2, 2, "silent");
          insertIndex -= 2;
        } else if (textBeforeOne === "{") {
          reactQuillRef.deleteText(insertIndex - 1, 1, "silent");
          insertIndex -= 1;
        }

        reactQuillRef.insertEmbed(insertIndex, "spintaxChip", { options: validOptions }, "silent");
        reactQuillRef.setSelection(insertIndex + 1, "silent");
      }
      bodyOnChange(serializeEditorContent(reactQuillRef), "user");
    } catch { }
    setShowChipPopover(false);
    setChipPopoverData(null);
    chipElementRef.current = null;
  };

  const handleChipPopoverCancel = () => {
    if (!reactQuillRef) return;
    try {
      // If we were creating a NEW spintax triggered by keyboard, 
      // we only want to remove the trigger if it was '{{'.
      // If it was a single '{', we leave it alone so the user can continue 
      // typing to potentially form '{{' for the variations menu.
      if (!chipElementRef.current) {
        let currentIdx = triggerIndexRef.current;
        const textBeforeTwo = reactQuillRef.getText(currentIdx - 2, 2);

        if (textBeforeTwo === "{{") {
          reactQuillRef.deleteText(currentIdx - 2, 2, "silent");
        }
      }
    } catch (err) {
      console.error("Cancel spintax cleanup error:", err);
    }
    setShowChipPopover(false);
    setChipPopoverData(null);
    chipElementRef.current = null;
  };

  const handleWorkingDaysApply = () => {
    if (!reactQuillRef || !workingDaysChipRef.current) return;
    const count = Math.max(1, Math.floor(Number(workingDaysCount) || 1));
    const tokenName = workingDaysType === "working"
      ? `${count} working days from today`
      : `${count} days from today`;
    try {
      const blot = Quill.find(workingDaysChipRef.current);
      if (blot) {
        const index = reactQuillRef.getIndex(blot);
        reactQuillRef.deleteText(index, 1, "silent");
        reactQuillRef.insertEmbed(index, "workingDaysChip", tokenName, "silent");
        reactQuillRef.setSelection(index + 1, "silent");
        bodyOnChange(serializeEditorContent(reactQuillRef), "user");
      }
    } catch (err) {
      console.error("Working days apply error:", err);
    }
    setShowWorkingDaysPopover(false);
    workingDaysChipRef.current = null;
  };

  const loadAttachmentIcon = () => {
    if (
      ["email", "manualemail", "templates"].includes(stageType?.toLowerCase())
    ) {
      const templateToolbarList = document.querySelectorAll(
        "#templateQuilEditor > div.quillEditorContainer > div.quill > div.ql-toolbar.ql-snow > span:nth-child(2)"
      );
      const sequenceToolbarList = document.querySelectorAll(
        "#sequenceAutomaticQuilEditor > div.quillEditorContainer > div.quill > div.ql-toolbar.ql-snow > span:nth-child(2)"
      );
      const emailComposeEditorList = document.querySelectorAll(
        "#emailComposeEditor > div.quillEditorContainer > div.quill > div.ql-toolbar.ql-snow > span:nth-child(2)"
      );
      const manualEmailPerformTaskEditorList = document.querySelectorAll(
        ".performTaskManualEmailEditor > div.quillEditorContainer > div.quill > div.ql-toolbar.ql-snow > span:nth-child(2)"
      );
      const accountNotesEditorList = document.querySelectorAll(
        "#accountNotesEditor > div.quillEditorContainer > div.quill > div.ql-toolbar.ql-snow > span:nth-child(2)"
      );
      if (
        templateToolbarList?.length ||
        sequenceToolbarList?.length ||
        emailComposeEditorList?.length ||
        manualEmailPerformTaskEditorList?.length ||
        accountNotesEditorList?.length
      ) {
        const parent = document.createElement("button");
        parent.setAttribute("id", "attachmentUploadFileDom");
        if (templateToolbarList?.length) {
          templateToolbarList[0].appendChild(parent);
        } else if (sequenceToolbarList?.length) {
          sequenceToolbarList[0].appendChild(parent);
        } else if (emailComposeEditorList?.length) {
          emailComposeEditorList[0].appendChild(parent);
        } else if (manualEmailPerformTaskEditorList?.length) {
          manualEmailPerformTaskEditorList[0].appendChild(parent);
        } else if (accountNotesEditorList?.length) {
          accountNotesEditorList[0].appendChild(parent);
        }
        const attachFileId = document.getElementById("attachmentUploadFileDom");
        ReactDOM.render(
          <FileUpload fromTaskPage={fromTaskPage} />,
          attachFileId
        );
      }
    }
  };

  const loadTracking = () => {
    if (!fromTaskPage && type === "reply") {
      const toolbarTrackingDom = document.querySelectorAll(
        "#emailComposeEditor .quill > div.ql-toolbar.ql-snow > span:last-child"
      );
      if (toolbarTrackingDom?.length) {
        const dom = document.createElement("span");
        dom.setAttribute("id", "toolbarTracking");
        toolbarTrackingDom[0].appendChild(dom);
        const addToolbarTrackingDom = document.getElementById("toolbarTracking");
        ReactDOM.render(
          <ToolbarTrackingIcon type={type} />,
          addToolbarTrackingDom
        );
      }
    }
  };

  const loadRewriteAi = () => {
    if (showRewriteAI) {
      const rewriteAiDom = document.querySelector(".quill");
      if (rewriteAiDom) {
        let dom = document.getElementById("chatGptEditorSection");
        if (!dom) {
          dom = document.createElement("span");
          dom.setAttribute("id", "chatGptEditorSection");
          dom.setAttribute("class", "chatGptEditorSection");
          rewriteAiDom.appendChild(dom);
        }
        const addRewriteAIDom = dom;
        let attachmentHeight =
          document.getElementById("editorFileAttachment")?.clientHeight || 0;
        addRewriteAIDom.style.bottom = 40 + attachmentHeight;
        ReactDOM.render(
          <RewriteAi
            updateContentCbk={updateContentCbk}
            pageType={pageType}
            fromTaskPage={fromTaskPage}
            editorRef={reactQuillRef}
            emailType={emailType}
            emailSubject={emailSubject}
            replyAsThread={replyAsThread}
          />,
          addRewriteAIDom
        );
      }
    }
  };

  const handleRefAction = (el = "") => {
    if (el) {
      attachQuillRefs(el);
      setTimeout(() => {
        loadPrevContent();
      }, 100);
    }
  };

  const loadPerformTaskFileUploadingLoader = () => {
    if (fromTaskPage) {
      const quilDom = document.querySelectorAll("#reactEditor .quill");
      if (quilDom?.length) {
        const dom = document.createElement("div");
        dom.setAttribute("id", "performTaskFileUploadingLoader");
        quilDom[0].appendChild(dom);
        const fileUploadingLoaderDOm = document.getElementById(
          "performTaskFileUploadingLoader"
        );
        ReactDOM.render(
          <PerformTaskFileUploadingLoader />,
          fileUploadingLoaderDOm
        );
      }
    }
  };

  const loadPerformTaskFileAttachments = () => {
    if (fromTaskPage) {
      const attachmentDom = document.querySelectorAll(
        "#reactEditor .quill > div.ql-container"
      );
      if (attachmentDom?.length) {
        const dom = document.createElement("div");
        dom.setAttribute("id", "performTaskFileAttachment");
        attachmentDom[0].appendChild(dom);
        const addAttachmentDom = document.getElementById(
          "performTaskFileAttachment"
        );
        ReactDOM.render(
          <PerformTaskFileAttachment attachments={attachments} />,
          addAttachmentDom
        );
      }
    }
  };

  const loadPrevContent = () => {
    const showReplyThread =
      replyContent && ["REPLY", "REPLY_ALL"].includes(emailType);
    if (showReplyThread) {
      const quillElement =
        document.querySelector(".quill") ||
        document.querySelector("#emailComposeEditor .quill") ||
        document.querySelector("#reactEditor .quill");

      if (quillElement) {
        const quillContainer = quillElement.querySelector("div.ql-container");

        if (quillContainer) {
          const qlEditor = quillContainer.querySelector(".ql-editor");

          if (qlEditor && quillContainer.contains(qlEditor)) {
            if (qlEditor.parentElement !== quillContainer) {
              quillContainer.appendChild(qlEditor);
            }
            const existingPrevContent = document.getElementById(
              "prevContentInsideQuill"
            );
            const shouldRecreate =
              !existingPrevContent ||
              (previousReplyContentRef.current &&
                previousReplyContentRef.current !== replyContent);

            if (shouldRecreate) {
              if (existingPrevContent) {
                ReactDOM.unmountComponentAtNode(existingPrevContent);
                existingPrevContent.remove();
                const qlEditorToReset =
                  quillContainer.querySelector(".ql-editor");
                if (qlEditorToReset) {
                  qlEditorToReset.style.minHeight = "";
                  qlEditorToReset.style.maxHeight = "";
                  qlEditorToReset.style.overflowY = "";
                }
                const emailPreviewCard = document.querySelector(
                  ".emailPreviewContentPopup .card"
                );
                if (emailPreviewCard) {
                  emailPreviewCard.style.maxHeight = "";
                  emailPreviewCard.style.overflowY = "";
                }
                if (quillContainer) {
                  quillContainer.style.maxHeight = "";
                  quillContainer.style.height = "";
                }
              }

              const dom = document.createElement("div");
              dom.setAttribute("id", "prevContentInsideQuill");
              dom.setAttribute("class", "prevContentInsideQuill");

              if (qlEditor.nextSibling) {
                quillContainer.insertBefore(dom, qlEditor.nextSibling);
              } else {
                quillContainer.appendChild(dom);
              }
              const prevContentDom = document.getElementById(
                "prevContentInsideQuill"
              );

              const initialExpandedState =
                previousReplyContentExpandedRef.current;

              ReactDOM.render(
                <CollapsiblePrevContent
                  content={replyContent}
                  initialExpanded={initialExpandedState}
                  lastUpdatedTs={lastUpdatedTs}
                  repliedFromAddress={repliedFromAddress}
                  onExpandChange={isExpanded => {
                    previousReplyContentExpandedRef.current = isExpanded;

                    if (isExpanded) {
                      qlEditor.style.minHeight = "100px";
                      qlEditor.style.maxHeight = "120px";
                      qlEditor.style.overflowY = "auto";
                      const emailComposeEditor =
                        document.getElementById("emailComposeEditor");
                      if (emailComposeEditor) {
                        emailComposeEditor.style.height = "400px";
                      }
                    } else {
                      qlEditor.style.minHeight = "";
                      qlEditor.style.maxHeight = "";
                      qlEditor.style.overflowY = "";

                      const emailComposeEditor =
                        document.getElementById("emailComposeEditor");
                      if (emailComposeEditor) {
                        emailComposeEditor.style.height = "";
                      }
                    }
                  }}
                />,
                prevContentDom
              );

              previousReplyContentRef.current = replyContent;
            }
          }
        }
      }
    } else {
      const existingPrevContent = document.getElementById(
        "prevContentInsideQuill"
      );
      if (existingPrevContent) {
        ReactDOM.unmountComponentAtNode(existingPrevContent);
        existingPrevContent.remove();
        const quillElement =
          document.querySelector(".quill") ||
          document.querySelector("#emailComposeEditor .quill") ||
          document.querySelector("#reactEditor .quill");
        if (quillElement) {
          const quillContainer = quillElement.querySelector("div.ql-container");
          if (quillContainer) {
            const qlEditor = quillContainer.querySelector(".ql-editor");
            if (qlEditor) {
              qlEditor.style.minHeight = "";
              qlEditor.style.maxHeight = "";
              qlEditor.style.overflowY = "";
            }
            quillContainer.style.maxHeight = "";
            quillContainer.style.height = "";
          }
        }
        previousReplyContentExpandedRef.current = false;
        previousReplyContentRef.current = null;
        const emailPreviewCard = document.querySelector(
          ".emailPreviewContentPopup .card"
        );
        if (emailPreviewCard) {
          emailPreviewCard.style.maxHeight = "";
          emailPreviewCard.style.overflowY = "";
        }
      }
    }
  };

  const handleDrop = e => {
    e.preventDefault();
    emailEditor.setDropFile(true);
  };

  const handlePaste = e => {
    emailEditor.setDropFile(true);
    let imgCount = 0;
    const windowItems = e.clipboardData.items;
    for (let i = 0; i < windowItems.length; i++) {
      if (windowItems[i].type.startsWith("image")) {
        e.preventDefault();
        emailEditor.setImgCount(imgCount++);
        if (emailEditor?.imgCount > 1) {
          return;
        }
      }
    }
  };

  useEffect(() => {
    bindCustomEvent();
    loadAttachmentIcon();
    !isOutboxPage && loadTracking();
    if (fromTaskPage) {
      loadPerformTaskFileAttachments();
      loadPerformTaskFileUploadingLoader();
    }
    return () => {
      reactQuillRef = null;
      if (fromTaskPage) {
        writingAssistantStoreData.setTaskBodyContent("");
        taskQuillRef = null;
      } else {
        writingAssistantStoreData.setInputValue({ ...defaultInputValue });
        writingAssistantStoreData.setBodyContent("");
        quillRef = null;
      }
      const existingPrevContent = document.getElementById(
        "prevContentInsideQuill"
      );
      if (existingPrevContent) {
        ReactDOM.unmountComponentAtNode(existingPrevContent);
        existingPrevContent.remove();
        const emailPreviewCard = document.querySelector(
          ".emailPreviewContentPopup .card"
        );
        if (emailPreviewCard) {
          emailPreviewCard.style.maxHeight = "";
          emailPreviewCard.style.overflowY = "";
        }
      }
    };
  }, []);

  useEffect(() => {
    loadRewriteAi();
  }, [emailSubject, emailType, replyAsThread, showRewriteAI]);

  useEffect(() => {
    loadPrevContent();
  }, [replyContent, emailType]);

  useEffect(() => {
    prefillData();
  }, [prefilledValue]);

  useEffect(() => {
    setFileUploading(fileAttachmentStore?.fileUploadingLoader || false);
  }, [fileAttachmentStore?.fileUploadingLoader]);

  useEffect(() => {
    const qlEditor = document.querySelector(".ql-editor");
    if (qlEditor) {
      qlEditor.addEventListener("drop", handleDrop);
      qlEditor.addEventListener("paste", handlePaste);
      return () => {
        qlEditor.removeEventListener("drop", handleDrop);
        qlEditor.removeEventListener("paste", handlePaste);
      };
    }
    checkConditionLimit();
  }, []);

  useEffect(() => {
    const handleInsertLiquid = (e) => {
      const currentQuill = fromTaskPage ? taskQuillRef : quillRef;
      if (currentQuill && e.detail) {
        try {
          const { liquidStr } = e.detail;
          const insertIndex = currentQuill.getSelection()?.index || currentQuill.getLength();

          currentQuill.clipboard.dangerouslyPasteHTML(insertIndex, liquidStr, "user");
          bodyOnChange(serializeEditorContent(currentQuill), "user");
        } catch (err) {
          console.error("Error inserting liquid string:", err);
        }
      }
    };

    window.addEventListener("insertLiquidString", handleInsertLiquid);
    return () => window.removeEventListener("insertLiquidString", handleInsertLiquid);
  }, [fromTaskPage, bodyOnChange]);

  useEffect(() => {
    const handleInsertConditionBlock = (e) => {
      const currentQuill = fromTaskPage ? taskQuillRef : quillRef;
      if (!currentQuill || !e.detail) return;
      const { branches, fallback } = e.detail;
      const existingBlots = currentQuill.root.querySelectorAll(".condition-block-blot");
      if (existingBlots.length >= 3) return;
      const index = conditionInsertIndex ?? currentQuill.getSelection()?.index ?? currentQuill.getLength();
      conditionInsertIndex = null;
      currentQuill.insertEmbed(index, "conditionBlock", { branches, fallback }, "user");
      currentQuill.setSelection(index + 1, 0);
      bodyOnChange(serializeEditorContent(currentQuill), "user");
      checkConditionLimit();
    };
    window.addEventListener("insertConditionBlock", handleInsertConditionBlock);
    return () => window.removeEventListener("insertConditionBlock", handleInsertConditionBlock);
  }, [fromTaskPage, bodyOnChange]);

  // Chip click handler — opens popover anchored to the clicked chip
  useEffect(() => {
    const qlEditor = document.querySelector(".ql-editor");
    if (!qlEditor) return;

    const handleEditorClick = (e) => {
      const spintaxChip = e.target.closest(".spintax-chip-blot");
      const workingDaysChip = e.target.closest(".working-days-chip-blot");
      const varChip = !workingDaysChip && e.target.closest(".variable-chip-blot");
      if (spintaxChip) {
        e.stopPropagation();
        try {
          const options = JSON.parse(spintaxChip.getAttribute("data-options") || "[]");
          const chipRect = spintaxChip.getBoundingClientRect();
          const container = document.querySelector(".quillEditorContainer");
          const containerRect = container
            ? container.getBoundingClientRect()
            : { top: 0, left: 0 };

          let top = chipRect.bottom - containerRect.top + 6;
          let left = chipRect.left - containerRect.left;
          const maxLeft = (container?.offsetWidth || 800) - 320;
          if (left > maxLeft) left = maxLeft;
          if (left < 0) left = 10;

          chipElementRef.current = spintaxChip;
          setChipPopoverData({ options: [...options] });
          setChipPopoverPosition({ top, left });
          setShowChipPopover(true);
        } catch { }
      } else if (workingDaysChip) {
        e.stopPropagation();
        try {
          const dataName = workingDaysChip.getAttribute("data-name") || "";
          const numMatch = dataName.match(/^(\d+) (working days|days) from today$/i);
          const currentCount = numMatch ? parseInt(numMatch[1], 10) : 1;

          const chipRect = workingDaysChip.getBoundingClientRect();
          const container = document.querySelector(".quillEditorContainer");
          const containerRect = container ? container.getBoundingClientRect() : { top: 0, left: 0 };

          let top = chipRect.bottom - containerRect.top + 6;
          let left = chipRect.left - containerRect.left;
          const maxLeft = (container?.offsetWidth || 800) - 240;
          if (left > maxLeft) left = maxLeft;
          if (left < 0) left = 10;

          workingDaysChipRef.current = workingDaysChip;
          setWorkingDaysCount(currentCount);
          setWorkingDaysType(dataName.includes("working days") ? "working" : "calendar");
          setWorkingDaysPopoverPosition({ top, left });
          setShowWorkingDaysPopover(true);
        } catch (err) {
          console.error("Working days chip click error:", err);
        }
      } else if (varChip) {
        e.stopPropagation();
        const blot = Quill.find(varChip);
        if (blot && reactQuillRef) {
          try {
            const index = reactQuillRef.getIndex(blot);
            const chipRect = varChip.getBoundingClientRect();
            const container = document.querySelector(".quillEditorContainer");
            const containerRect = container
              ? container.getBoundingClientRect()
              : { top: 0, left: 0 };

            let top = chipRect.bottom - containerRect.top + 6;
            let left = chipRect.left - containerRect.left;
            const maxLeft = (container?.offsetWidth || 800) - 250;
            if (left > maxLeft) left = maxLeft;
            if (left < 0) left = 10;

            triggerIndexRef.current = index;
            setTriggerType("chip"); // Use new 'chip' type to replace on selection
            setTokenPopupPosition({ top, left });
            setshowVariableCont(true);

            // Don't delete immediately, just set the selection near it
            reactQuillRef.setSelection(index, 1, "silent");
          } catch (err) {
            console.error("Variable chip click error:", err);
          }
        }
      }
    };

    qlEditor.addEventListener("click", handleEditorClick);
    return () => qlEditor.removeEventListener("click", handleEditorClick);
  }, []);

  const handleRemoveSpintax = () => {
    if (!reactQuillRef || !chipElementRef.current) return;
    try {
      const blot = Quill.find(chipElementRef.current);
      if (blot) {
        const index = reactQuillRef.getIndex(blot);
        reactQuillRef.deleteText(index, 1, "user");
        bodyOnChange(serializeEditorContent(reactQuillRef), "user");
      }
    } catch (err) {
      console.error("Remove spintax error:", err);
    }
    setShowChipPopover(false);
    setChipPopoverData(null);
    chipElementRef.current = null;
  };

  return (
    <div className="quillEditorContainer">
      {showVariationsDropdown && (
        <div
          className="variationsDropdownCard"
          style={{
            top: popupPosition.top,
            left: popupPosition.left,
            bottom: 'auto',
            right: 'auto'
          }}
        >
          <div
            className="variationsDropdownItem"
            onMouseEnter={() => {
              clearHoverTimeout();
              let left = popupPosition.left + 270;
              setTokenPopupPosition({ top: popupPosition.top, left });
              setshowVariableCont(true);
            }}
            onMouseLeave={startHoverTimeout}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <div className="iconWrapper">
              <VariablesIcon />
            </div>
            <div className="textWrapper">
              <div className="title">Variables</div>
              <div className="description">Personalize your email</div>
            </div>
            <div className="chevronWrapper">
              <DownArrow />
            </div>
          </div>
          <div
            className="variationsDropdownItem"
            onMouseEnter={() => {
              clearHoverTimeout();
              setshowVariableCont(false);
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              const currentRange = reactQuillRef?.getSelection();
              const currentTriggerType = triggerType;
              let deletionIndex = triggerIndexRef.current;

              if (currentTriggerType === "toolbar" && currentRange) {
                deletionIndex = currentRange.index;
              }

              if (currentTriggerType === "keyboard") {
                const textBefore = reactQuillRef.getText(deletionIndex - 2, 2);
                if (textBefore === "{{") {
                  reactQuillRef.deleteText(deletionIndex - 2, 2);
                }
              }
              setTriggerType(null);
              writingAssistantStoreData.setShowRewriteAiCard(true);
              setShowVariationsDropdown(false);
            }}
          >
            <div className="iconWrapper">
              <WriteWithAiIcon />
            </div>
            <div className="textWrapper">
              <div className="title">Write with AI</div>
              <div className="description">Rewrite with variations</div>
            </div>
          </div>
          <div
            className={`variationsDropdownItem ${disableSpintaxAndCondition ? "disabled" : ""}`}
            onMouseEnter={() => {
              clearHoverTimeout();
              setshowVariableCont(false);
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (disableSpintaxAndCondition) return;
              handleSpintaxClick();
              setShowVariationsDropdown(false);
            }}
          >
            <div className="iconWrapper">
              <SpintaxIcon />
            </div>
            <div className="textWrapper">
              <div className="title">Spintax</div>
              <div className="description">Add text options</div>
            </div>
          </div>
          {showAddCondition && (
            <div
              className={`variationsDropdownItem ${isLimitReached || disableSpintaxAndCondition ? "disabled" : ""}`}
              onMouseEnter={() => {
                if (isLimitReached) return;
                clearHoverTimeout();
                setshowVariableCont(false);
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (isLimitReached || disableSpintaxAndCondition) return;
                const _cq = fromTaskPage ? taskQuillRef : quillRef;
                // Use triggerIndexRef.current — getSelection() is null after clicking the dropdown
                let insertIdx = triggerIndexRef.current;
                if (triggerType === "keyboard" && _cq) {
                  const textBefore = _cq.getText(insertIdx - 2, 2);
                  if (textBefore === "{{") {
                    _cq.deleteText(insertIdx - 2, 2, "silent");
                    insertIdx -= 2;
                  }
                }
                conditionInsertIndex = insertIdx;
                setTriggerType(null);
                setShowVariationsDropdown(false);
                window.dispatchEvent(new CustomEvent("openConditionBuilder"));
              }}
              title={isLimitReached ? "Maximum 3 conditional blocks allowed" : ""}
            >
              <div className="iconWrapper">
                <i className="material-icons" style={{ color: "black", fontSize: "20px" }}>add_circle_outline</i>
              </div>
              <div className="textWrapper">
                <div className="title">Add Condition</div>
                <div className="description">
                  {isLimitReached ? "Maximum limit reached" : "Add logic-based content"}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {showVariationsDropdown && (
        <div
          className="variationsOverlay"
          onClick={handleCloseVariations}
        />
      )}
      {!fromTaskPage && showVariableCont && (
        <div
          className="tokenVariableWrapper"
          onMouseEnter={clearHoverTimeout}
          onMouseLeave={startHoverTimeout}
          style={{
            top: tokenPopupPosition.top,
            left: tokenPopupPosition.left,
            position: 'absolute',
            zIndex: 1002
          }}
        >
          <TokenVariable
            tokenCbk={optionAction}
            closeCbk={handleCloseVariables}
            pageType={pageType}
            showInboxTokens={showInboxTokens}
            showDateTokens={showDateTokens}
            inboxIds={inboxIds}
            hideMask={true}
          />
        </div>
      )}

      <ReactQuill
        placeholder={placeholder}
        modules={editorModules}
        onChange={handleChange}
        onFocus={e => handleFocus(e)}
        spellcheck={false}
        theme="snow"
        ref={el => handleRefAction(el)}
      />
      {!fromTaskPage && attachments?.length > 0 && (
        <FileAttachmentList
          attachments={attachments}
          entityObj={entityObj}
          fromTaskPage={fromTaskPage}
        />
      )}
      {fileUploading && !fromTaskPage && <FileUploadingLoader />}

      {/* Working days chip popover */}
      {showWorkingDaysPopover && (
        <>
          <div className="chipPopoverOverlay" onClick={() => setShowWorkingDaysPopover(false)} />
          <div
            className="workingDaysChipPopover"
            style={{ top: workingDaysPopoverPosition.top, left: workingDaysPopoverPosition.left }}
            onClick={e => e.stopPropagation()}
          >
            <div className="chipPopoverTitle">{workingDaysType === "working" ? "Working days from today" : "Days from today"}</div>
            <div className="chipVariantCard">
              <input
                type="number"
                className="workingDaysInput"
                min="1"
                max="365"
                value={workingDaysCount}
                autoFocus
                onChange={e => setWorkingDaysCount(e.target.value)}
              />
            </div>
            <div className="chipPopoverActions">
              <button className="chipPopoverCancel" onClick={() => setShowWorkingDaysPopover(false)}>Cancel</button>
              <button className="chipPopoverApply" onClick={handleWorkingDaysApply}>Apply</button>
            </div>
          </div>
        </>
      )}

      {/* Chip popover — shown when a spintax chip is clicked */}
      {showChipPopover && chipPopoverData && (
        <>
          <div
            className="chipPopoverOverlay"
            onClick={handleChipPopoverCancel}
          />
          <div
            className="spintaxChipPopover"
            style={{ top: chipPopoverPosition.top, left: chipPopoverPosition.left }}
            onClick={e => e.stopPropagation()}
          >
            <div className="chipPopoverHeader">
              <div className="chipPopoverTitle">Variants — one is sent randomly</div>
              <button
                className="chipPopoverDelete"
                onClick={handleRemoveSpintax}
                title="Remove Spintax"
              >
                Delete
              </button>
            </div>
            {chipPopoverData.options.map((opt, i) => (
              <div
                key={i}
                className={`chipVariantCard`}
              >
                <textarea
                  className="chipVariantInput"
                  value={opt}
                  placeholder={`Variant ${i + 1}`}
                  rows={2}
                  onChange={e => {
                    const newOptions = [...chipPopoverData.options];
                    newOptions[i] = e.target.value;
                    setChipPopoverData({ ...chipPopoverData, options: newOptions });
                  }}
                  autoFocus={i === 0}
                />
                {chipPopoverData.options.length > 2 && (
                  <button
                    className="chipVariantDelete"
                    onClick={() => {
                      const newOptions = chipPopoverData.options.filter((_, j) => j !== i);
                      setChipPopoverData({ ...chipPopoverData, options: newOptions });
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            {chipPopoverData.options.length < 3 && (
              <div
                className="chipPopoverAdd"
                onClick={() =>
                  setChipPopoverData({
                    ...chipPopoverData,
                    options: [...chipPopoverData.options, ""]
                  })
                }
              >
                + Add variant
              </div>
            )}
            <div className="chipPopoverActions">
              <button
                className="chipPopoverCancel"
                onClick={handleChipPopoverCancel}
              >
                Cancel
              </button>
              <button
                className="chipPopoverApply"
                onClick={handleChipPopoverApply}
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default observer(QuilEditor);
export { QuilEditor };
